import React, { useState } from 'react';
import { ChevronRight, ClipboardList } from 'lucide-react';
import type { TaskStatusPart, TaskItem } from '../../types/chat';

interface InlineTaskStatusProps {
  part: TaskStatusPart;
  isStreaming?: boolean;
  onExpand?: () => void;
}

const statusIcons: Record<TaskItem['status'], string> = {
  pending: '\u25CB',
  in_progress: '\u25D4',
  completed: '\u2713',
  failed: '\u2717',
  skipped: '\u2298',
};

const statusColors: Record<TaskItem['status'], string> = {
  pending: 'text-gray-400 dark:text-gray-500',
  in_progress: 'text-sky-500',
  completed: 'text-green-500',
  failed: 'text-red-500',
  skipped: 'text-gray-400 dark:text-gray-500',
};

export function InlineTaskStatus({
  part,
  isStreaming = false,
  onExpand,
}: InlineTaskStatusProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const currentTask = part.currentTaskId
    ? part.tasks.find((t) => t.id === part.currentTaskId)
    : part.tasks.find((t) => t.status === 'in_progress');

  const progressPercent =
    part.totalCount > 0
      ? Math.round((part.completedCount / part.totalCount) * 100)
      : 0;

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  return (
    <div
      className={`my-1 rounded-lg border bg-gray-50 dark:bg-gray-800/50 overflow-hidden transition-all duration-300 ${
        isStreaming ? 'border-sky-200 dark:border-sky-800 shadow-sm' : 'border-gray-200 dark:border-gray-700'
      }`}
      data-testid="inline-task-status"
      data-plan-id={part.planId}
    >
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-2 text-left bg-transparent border-0 hover:bg-gray-100 dark:hover:bg-gray-800"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
      >
        <ClipboardList
          className={`w-4 h-4 flex-shrink-0 ${isStreaming ? 'text-sky-500' : 'text-gray-500 dark:text-gray-400'}`}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-200">
              Plan: {part.completedCount}/{part.totalCount} tasks
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{progressPercent}%</span>
          </div>

          <div className="mt-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                isStreaming ? 'bg-sky-500' : 'bg-green-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {currentTask && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs">
              <span
                className={`${statusColors[currentTask.status]} ${
                  currentTask.status === 'in_progress' ? 'animate-pulse' : ''
                }`}
              >
                {statusIcons[currentTask.status]}
              </span>
              <span className="text-gray-600 dark:text-gray-300 truncate">
                {currentTask.description}
              </span>
            </div>
          )}
        </div>

        <ChevronRight
          className={`w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform ${
            isExpanded ? 'rotate-90' : ''
          }`}
        />
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="space-y-1 pt-2">
            {part.tasks.map((task) => (
              <div
                key={task.id}
                className={`flex items-center gap-2 py-1 text-xs ${
                  task.id === part.currentTaskId
                    ? 'bg-sky-50 dark:bg-sky-900/20 -mx-1 px-1 rounded'
                    : ''
                }`}
                data-testid={`task-item-${task.id}`}
              >
                <span
                  className={`${statusColors[task.status]} ${
                    task.status === 'in_progress' ? 'animate-pulse' : ''
                  }`}
                >
                  {statusIcons[task.status]}
                </span>
                <span
                  className={`flex-1 truncate ${
                    task.status === 'completed'
                      ? 'text-gray-500 dark:text-gray-400 line-through'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {task.description}
                </span>
                {task.progress !== undefined &&
                    task.status === 'in_progress' && (
                    <span className="text-sky-500">{task.progress}%</span>
                  )}
              </div>
            ))}
          </div>

          {onExpand && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExpand();
              }}
              className="mt-2 text-xs text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 flex items-center gap-1"
            >
              View full plan
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
